/**
 * Inspector section for a `ZONE_SLIDER` widget — its DESIGN tier.
 *
 * Grouped controls (value & range, number format, sub-label, badge, style)
 * rendered through the generic `DesignControlRendererComponent`, plus the one
 * thing that cannot be a flat control: the editable list of zones (colour,
 * label, upper bound). Everything is written to `widget.appearance`; the host
 * folds it into the control's `[data]` like any other appearance key.
 *
 * Dataset-driven value / zones are the DATA tier (Data tab); when the zones
 * come from rows, the list here is only the fallback and says so.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import type { FormatControl } from '../../../shared/self-service-config.types';
import {
  ZONE_SLIDER_DEFAULTS,
  ZONE_SLIDER_PALETTE,
  finiteOrNull,
  normaliseZones,
  type ZoneSliderZone,
} from '../../../../global-components/zone-slider/zone-slider.utils';
import { DesignControlRendererComponent } from './design-control-renderer.component';

interface SettingsGroup {
  name: string;
  controls: FormatControl[];
}

const D = ZONE_SLIDER_DEFAULTS;

/** Declared in the shared `FormatControl` shape so the one renderer draws them. */
const GROUPS: SettingsGroup[] = [
  {
    name: 'Value & range',
    controls: [
      { key: 'label', label: 'Caption', control: 'text', default: D.label, placeholder: 'Shown above the slider' },
      { key: 'value', label: 'Value', control: 'number', default: D.value },
      { key: 'min', label: 'Minimum', control: 'number', default: D.min },
      { key: 'max', label: 'Maximum', control: 'number', default: D.max },
      { key: 'step', label: 'Step', control: 'number', default: D.step, min: 0 },
      { key: 'minPaddingPct', label: 'Extra room below start (%)', control: 'number', default: D.minPaddingPct, min: 0 },
      { key: 'maxPaddingPct', label: 'Extra room above end (%)', control: 'number', default: D.maxPaddingPct, min: 0 },    ],
  },
  {
    name: 'Number format',
    controls: [
      { key: 'prefix', label: 'Prefix', control: 'text', default: D.prefix, placeholder: '$' },
      { key: 'suffix', label: 'Suffix', control: 'text', default: D.suffix, placeholder: '%' },
      { key: 'numberFormat', label: 'Format', control: 'select', optionsKey: 'numberFormats', default: D.numberFormat },
      { key: 'decimals', label: 'Decimals', control: 'number', default: D.decimals, min: 0, max: 6 },
    ],
  },
  {
    name: 'Sub label',
    controls: [
      { key: 'subLabel', label: 'Text', control: 'text', default: D.subLabel, placeholder: '[{margin}% Margin]' },
      { key: 'cost', label: 'Cost basis', control: 'number', default: D.cost },
      { key: 'marginFormula', label: 'Margin formula', control: 'text', default: '', placeholder: '([Value] - [Cost]) / [Value] * 100' },
    ],
  },
  {
    name: 'Badge & labels',
    controls: [
      { key: 'showValue', label: 'Show value pill', control: 'toggle', default: D.showValue },
      { key: 'showBadge', label: 'Show zone badge', control: 'toggle', default: D.showBadge },
      { key: 'badgeText', label: 'Badge text', control: 'text', default: D.badgeText, placeholder: 'Active zone label' },
      { key: 'showRangeLabels', label: 'Show min / max', control: 'toggle', default: D.showRangeLabels },
      { key: 'showBoundaryValues', label: 'Show the value at every level boundary', control: 'toggle', default: false },
    ],
  },
  {
    name: 'Style',
    controls: [
      { key: 'pillBackground', label: 'Pill fill', control: 'color', default: D.pillBackground },
      { key: 'pillColor', label: 'Pill text', control: 'color', default: D.pillColor },
      { key: 'subLabelColor', label: 'Sub label', control: 'color', default: D.subLabelColor },
      { key: 'thumbColor', label: 'Thumb fill', control: 'color', default: D.thumbColor },
      { key: 'thumbBorderColor', label: 'Thumb ring', control: 'color', default: D.thumbBorderColor },
      { key: 'trackHeight', label: 'Track height', control: 'number', default: D.trackHeight, min: 2, max: 40 },
      { key: 'fontFamily', label: 'Font', control: 'select', optionsKey: 'fontFamilies', default: D.fontFamily },
      { key: 'fontSize', label: 'Font size', control: 'number', default: D.fontSize, min: 8, max: 40 },
    ],
  },
];

@Component({
  selector: 'app-design-zone-slider-settings',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatTooltipModule, DesignControlRendererComponent],
  templateUrl: './zone-slider-settings.component.html',
  styleUrl: './zone-slider-settings.component.scss',
})
export class ZoneSliderSettingsComponent {
  /** The widget's `appearance`. */
  readonly appearance = input<Record<string, unknown>>({});
  /** `layout.options`, for the select controls' option lists. */
  readonly options = input<Record<string, unknown>>({});
  /** The Data tab maps zones from dataset rows; the list here is then a fallback. */
  readonly zonesFromData = input<boolean>(false);
  /** A partial appearance patch — the inspector merges it. */
  readonly appearanceChange = output<Record<string, unknown>>();

  readonly groups = GROUPS;
  readonly palette = ZONE_SLIDER_PALETTE;

  private readonly openGroups = signal<ReadonlySet<string>>(new Set(['Value & range', 'Zones']));

  readonly zones = computed<ZoneSliderZone[]>(() => {
    const raw = this.appearance()['zones'];
    return Array.isArray(raw) ? normaliseZones(raw) : D.zones.map((zone) => ({ ...zone }));
  });

  isOpen(group: string): boolean {
    return this.openGroups().has(group);
  }

  toggle(group: string): void {
    this.openGroups.update((current) => {
      const next = new Set(current);
      if (!next.delete(group)) next.add(group);
      return next;
    });
  }

  onControlChange(change: { key: string; value: unknown }): void {
    this.appearanceChange.emit({ [change.key]: change.value });
  }

  // ── Zones ──────────────────────────────────────────────────────────────────

  zoneColor(zone: ZoneSliderZone, index: number): string {
    const color = zone.color ?? this.palette[index % this.palette.length];
    // <input type="color"> only takes #rrggbb.
    return /^#[0-9a-f]{6}$/i.test(color) ? color : '#667085';
  }

  setZone(index: number, patch: Partial<ZoneSliderZone>): void {
    const next = this.zones().map((zone, i) => (i === index ? { ...zone, ...patch } : zone));
    this.writeZones(next);
  }

  setZoneTo(index: number, raw: string): void {
    this.setZone(index, { to: finiteOrNull(raw) });
  }

  addZone(): void {
    const zones = this.zones();
    const index = zones.length;
    this.writeZones([...zones, { label: `Zone ${index + 1}`, color: this.palette[index % this.palette.length], to: null }]);
  }

  removeZone(index: number): void {
    this.writeZones(this.zones().filter((_, i) => i !== index));
  }

  moveZone(index: number, delta: -1 | 1): void {
    const next = [...this.zones()];
    const to = index + delta;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    this.writeZones(next);
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByControlKey(_: number, control: FormatControl): string {
    return control.key;
  }

  trackByGroupName(_: number, group: SettingsGroup): string {
    return group.name;
  }

  private writeZones(zones: ZoneSliderZone[]): void {
    this.appearanceChange.emit({ zones });
  }
}
